<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalServices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    rental: {
      type: Object,
      required: false,
    },
    meter: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const filterAndSortItems = (...args) => {
    const [items] = args;
    const visibleItems = (items || []).filter(
      (item) =>
        (filters.value.active && !item.inactive) ||
        (filters.value.inactive && item.inactive)
    );
    const sortKey = ['oldest', 'newest'].includes(sortOrder.value)
      ? 'created_at'
      : 'valid_from';
    const sortDir = ['oldest', 'validFromAsc'].includes(sortOrder.value)
      ? 'asc'
      : 'desc';
    return _.orderBy(visibleItems, [sortKey], [sortDir]);
  };
  const isServiceStale = (...args) => {
    const [service] = args;
    return (
      service.inactive ||
      (service.valid_to &&
        $fn.value.parseISODate(service.valid_to) < $fn.value.today)
    );
  };

  const items = ref(null);
  const filters = ref({
    active: true,
    inactive: false,
  });
  const sortOrder = ref('validFromAsc');
  const sortOptions = ref([
    {
      key: 'oldest',
      label: $fn.value.pgettext('RentalServices', 'Oldest first'),
      icon: 'fa-sort-numeric-up-alt',
    },
    {
      key: 'newest',
      label: $fn.value.pgettext('RentalServices', 'Newest first'),
      icon: 'fa-sort-numeric-down-alt',
    },
    {
      key: 'validFromAsc',
      label: $fn.value.pgettext('RentalServices', 'Valid from ascending'),
      icon: 'fa-sort-amount-up-alt',
    },
    {
      key: 'validFromDesc',
      label: $fn.value.pgettext('RentalServices', 'Valid from descending'),
      icon: 'fa-sort-amount-down-alt',
    },
  ]);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    filters: filters.value,
    sortOrder: sortOrder.value,
    sortOptions: sortOptions.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return props.rental
      ? $fn.value.fetch('list_rental_service', {
          parameters: { rental_id: props.rental.id },
        })
      : $fn.value.fetch('list_meter_rental_services', {
          parameters: { meter_id: props.meter.id },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      {
        name: 'filters',
        cache: { type: 'local', key: 'rental.contractServices.filters' },
      },
      {
        name: 'sortOrder',
        cache: { type: 'local', key: 'rental.contractServices.sortOrder' },
      },
      { name: 'sortOptions' },
      { name: 'ux' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, filters, sortOrder, sortOptions, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const assignToRental = computed(() => {
    return props.meter != null;
  });
  const preventEditMeter = computed(() => {
    return props.meter != null;
  });
  const preventEditRental = computed(() => {
    return props.rental != null;
  });
  const activeCount = computed(() => {
    if (loaded.value) {
      return (items.value || []).filter((item) => !item.inactive).length;
    }
    return null;
  });
  const inactiveCount = computed(() => {
    if (loaded.value) {
      return (items.value || []).filter((item) => item.inactive).length;
    }
    return null;
  });
  const visibleItems = computed(() => {
    if (loaded.value) {
      return filterAndSortItems(items.value);
    }
    return null;
  });
  const sortOrderLabel = computed(() => {
    if (loaded.value) {
      return (sortOptions.value.find((o) => o.key === sortOrder.value) || {})
        .label;
    }
    return null;
  });
  const sortOrderIcon = computed(() => {
    if (loaded.value) {
      return (sortOptions.value.find((o) => o.key === sortOrder.value) || {})
        .icon;
    }
    return null;
  });
  const showFilterToolbar = computed(() => {
    if (loaded.value) {
      return loaded && (items.value || []).length > 0;
    }
    return null;
  });

  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );
  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_2095089031469157_emit_0bryvf4sf_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_w3384ztk8_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_o75f71wnv_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalService',
      props: {
        rental: props.rental,
        meter: props.meter,
        assignToRental: assignToRental.value,
        preventEditMeter: preventEditMeter.value,
      },
    };
  };
  const act_4044930568678877_setData_0yeow283l_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'filters.active',
      value: !filters.value.active,
      resolve: false,
      noTouchLastLoaded: true,
      noTakeSnapshot: false,
    };
  };
  const act_1073564360475282_setData_4qbifa90a_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'filters.inactive',
      value: !filters.value.inactive,
      resolve: false,
      noTouchLastLoaded: true,
      noTakeSnapshot: false,
    };
  };
  const act_5074589820235598_emit_epkzr7eur_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5074589820235598_close_5p2zbmw1c_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_5074589820235598_showModal_ht2bswuhu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalService',
      props: {
        rental: props.rental,
        meter: props.meter,
        assignToRental: assignToRental.value,
        preventEditMeter: preventEditMeter.value,
      },
    };
  };
  const act_4067533979448964_setData_myie3tiux_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'sortOrder',
      value: $event.scope.option.key,
      resolve: false,
      noTouchLastLoaded: true,
      noTakeSnapshot: false,
    };
  };
  const act_682765494101086_emit_2m1nxxivj_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_682765494101086_close_70jjyk9up_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_682765494101086_showModal_hz1o4p0jm_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalService',
      props: {
        oid: $event.scope.item.id,
        mode: 'edit',
        meter: props.meter,
        rental: props.rental,
        assignToRental: assignToRental.value,
        preventEditMeter: preventEditMeter.value,
        preventEditRental: preventEditRental.value,
      },
    };
  };
  const act_7500099074451545_showModal_clrezntv4_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalServiceCostAllocator',
      props: {
        service: $event.scope.item,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_ekxz2wp89 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8kwa67ddh =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ekxz2wp89,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8kwa67ddh],
    event: 'reload',
  });
  const act_2095089031469157_emit_0bryvf4sf = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_0bryvf4sf_getActionArgs,
  });
  const inst_evh_2095089031469157_close_w3384ztk8 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_0bryvf4sf,
      event: 'close',
      when: inst_evh_2095089031469157_close_w3384ztk8_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_o75f71wnv =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_o75f71wnv_getActionArgs,
      events: [inst_evh_2095089031469157_close_w3384ztk8],
    });
  const inst_evh_2095089031469157_clickPreventStop_rx9wczt4t =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_o75f71wnv,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_rx9wczt4t],
      event: 'click.prevent.stop',
    });
  const act_4044930568678877_setData_0yeow283l = new actionTypes.SetDataAction({
    actionArgs: act_4044930568678877_setData_0yeow283l_getActionArgs,
  });
  const inst_evh_4044930568678877_clickPreventStop_zswq95vl9 =
    new actionTypes.EventHandlerImpl({
      action: act_4044930568678877_setData_0yeow283l,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_4044930568678877_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4044930568678877_clickPreventStop_zswq95vl9],
      event: 'click.prevent.stop',
    });
  const act_1073564360475282_setData_4qbifa90a = new actionTypes.SetDataAction({
    actionArgs: act_1073564360475282_setData_4qbifa90a_getActionArgs,
  });
  const inst_evh_1073564360475282_clickPreventStop_gnaib9ri2 =
    new actionTypes.EventHandlerImpl({
      action: act_1073564360475282_setData_4qbifa90a,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_1073564360475282_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1073564360475282_clickPreventStop_gnaib9ri2],
      event: 'click.prevent.stop',
    });
  const act_5074589820235598_emit_epkzr7eur = new actionTypes.EmitAction({
    actionArgs: act_5074589820235598_emit_epkzr7eur_getActionArgs,
  });
  const inst_evh_5074589820235598_close_5p2zbmw1c =
    new actionTypes.EventHandlerImpl({
      action: act_5074589820235598_emit_epkzr7eur,
      event: 'close',
      when: inst_evh_5074589820235598_close_5p2zbmw1c_getWhen,
      displayName: 'emit',
    });
  const act_5074589820235598_showModal_ht2bswuhu =
    new actionTypes.ShowModalAction({
      actionArgs: act_5074589820235598_showModal_ht2bswuhu_getActionArgs,
      events: [inst_evh_5074589820235598_close_5p2zbmw1c],
    });
  const inst_evh_5074589820235598_clickPreventStop_x295z5jkp =
    new actionTypes.EventHandlerImpl({
      action: act_5074589820235598_showModal_ht2bswuhu,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5074589820235598_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5074589820235598_clickPreventStop_x295z5jkp],
      event: 'click.prevent.stop',
    });
  const act_4067533979448964_setData_myie3tiux = new actionTypes.SetDataAction({
    actionArgs: act_4067533979448964_setData_myie3tiux_getActionArgs,
  });
  const inst_evh_4067533979448964_clickPrevent_29hns2cd4 =
    new actionTypes.EventHandlerImpl({
      action: act_4067533979448964_setData_myie3tiux,
      event: 'click.prevent',
      displayName: 'setData',
    });
  const inst_evh_4067533979448964_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4067533979448964_clickPrevent_29hns2cd4],
      event: 'click.prevent',
    });
  const act_682765494101086_emit_2m1nxxivj = new actionTypes.EmitAction({
    actionArgs: act_682765494101086_emit_2m1nxxivj_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_682765494101086_close_70jjyk9up =
    new actionTypes.EventHandlerImpl({
      action: act_682765494101086_emit_2m1nxxivj,
      event: 'close',
      when: inst_evh_682765494101086_close_70jjyk9up_getWhen,
      displayName: 'emitReload',
    });
  const act_682765494101086_showModal_hz1o4p0jm =
    new actionTypes.ShowModalAction({
      actionArgs: act_682765494101086_showModal_hz1o4p0jm_getActionArgs,
      events: [inst_evh_682765494101086_close_70jjyk9up],
    });
  const inst_evh_682765494101086_clickPreventStop_9ilul45fv =
    new actionTypes.EventHandlerImpl({
      action: act_682765494101086_showModal_hz1o4p0jm,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_682765494101086_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_682765494101086_clickPreventStop_9ilul45fv],
      event: 'click.prevent.stop',
    });
  const act_7500099074451545_showModal_clrezntv4 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7500099074451545_showModal_clrezntv4_getActionArgs,
    });
  const inst_evh_7500099074451545_clickPreventStop_uk429f64v =
    new actionTypes.EventHandlerImpl({
      action: act_7500099074451545_showModal_clrezntv4,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7500099074451545_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7500099074451545_clickPreventStop_uk429f64v],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_8kwa67ddh,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_w3384ztk8,
    inst_evh_2095089031469157_clickPreventStop_rx9wczt4t,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_4044930568678877_clickPreventStop_zswq95vl9,
    inst_evh_4044930568678877_clickPreventStop,
    inst_evh_1073564360475282_clickPreventStop_gnaib9ri2,
    inst_evh_1073564360475282_clickPreventStop,
    inst_evh_5074589820235598_close_5p2zbmw1c,
    inst_evh_5074589820235598_clickPreventStop_x295z5jkp,
    inst_evh_5074589820235598_clickPreventStop,
    inst_evh_4067533979448964_clickPrevent_29hns2cd4,
    inst_evh_4067533979448964_clickPrevent,
    inst_evh_682765494101086_close_70jjyk9up,
    emitReload: inst_evh_682765494101086_close_70jjyk9up,
    inst_evh_682765494101086_clickPreventStop_9ilul45fv,
    inst_evh_682765494101086_clickPreventStop,
    inst_evh_7500099074451545_clickPreventStop_uk429f64v,
    inst_evh_7500099074451545_clickPreventStop,
  };

  const evh_7315092382398562_reload_8kwa67ddh = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8kwa67ddh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_w3384ztk8 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_w3384ztk8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_rx9wczt4t = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_rx9wczt4t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_clickPreventStop_zswq95vl9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4044930568678877_clickPreventStop_zswq95vl9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4044930568678877_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4044930568678877_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1073564360475282_clickPreventStop_gnaib9ri2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1073564360475282_clickPreventStop_gnaib9ri2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1073564360475282_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1073564360475282_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_close_5p2zbmw1c = async (event, scope) => {
    return await actions.inst_evh_5074589820235598_close_5p2zbmw1c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_clickPreventStop_x295z5jkp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5074589820235598_clickPreventStop_x295z5jkp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5074589820235598_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5074589820235598_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4067533979448964_clickPrevent_29hns2cd4 = async (event, scope) => {
    return await actions.inst_evh_4067533979448964_clickPrevent_29hns2cd4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4067533979448964_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_4067533979448964_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_682765494101086_close_70jjyk9up = async (event, scope) => {
    return await actions.inst_evh_682765494101086_close_70jjyk9up.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_682765494101086_clickPreventStop_9ilul45fv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_682765494101086_clickPreventStop_9ilul45fv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_682765494101086_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_682765494101086_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7500099074451545_clickPreventStop_uk429f64v = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7500099074451545_clickPreventStop_uk429f64v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7500099074451545_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7500099074451545_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard v-if="meter != null">
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('RentalServices', 'Rental Services') }}
          </strong>
          <ItpButton
            :text="pgettext('RentalServices', 'Add rental service...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
    </ItpWidgetCard>
    <div class="card">
      <div class="card-header d-flex align-items-center gap-2 flex-wrap">
        <strong class="me-2" v-if="meter == null">
          {{ pgettext('RentalServices', 'Rental Services') }}
        </strong>
        <ItpButton
          variant="link"
          size="sm"
          no-wrap
          class="text-decoration-none"
          v-if="showFilterToolbar && activeCount > 0"
          @click.prevent.stop="
            evh_4044930568678877_clickPreventStop($event, {})
          "
        >
          <span
            :class="
              filters.active ? 'fw-bold text-body' : 'text-muted fw-normal'
            "
          >
            {{ pgettext('RentalServices', 'Active') }}
          </span>
          <span class="badge text-bg-secondary ms-2">
            {{ activeCount }}
          </span>
        </ItpButton>
        <ItpButton
          variant="link"
          size="sm"
          no-wrap
          class="text-decoration-none"
          v-if="showFilterToolbar && inactiveCount > 0"
          @click.prevent.stop="
            evh_1073564360475282_clickPreventStop($event, {})
          "
        >
          <span
            :class="
              filters.inactive ? 'fw-bold text-body' : 'text-muted fw-normal'
            "
          >
            {{ pgettext('RentalServices', 'Inactive') }}
          </span>
          <span class="badge text-bg-secondary ms-2">
            {{ inactiveCount }}
          </span>
        </ItpButton>
        <div v-if="meter == null">
          <ItpButton
            :text="pgettext('RentalServices', 'Add rental service...')"
            variant="light"
            size="sm"
            @click.prevent.stop="
              evh_5074589820235598_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </div>
        <div
          class="ms-auto d-flex flex-wrap align-items-center gap-2"
          v-if="showFilterToolbar"
        >
          <BDropdown
            size="sm"
            variant="light"
            toggle-class="border"
            auto-close="outside"
          >
            <template #button-content>
              <span>
                {{ pgettext('RentalServices', 'Status') }}
              </span>
            </template>
            <BDropdownForm>
              <BFormCheckbox v-model="filters.active" class="mb-2">
                {{ pgettext('RentalServices', 'Active') }}
              </BFormCheckbox>
              <BFormCheckbox v-model="filters.inactive">
                {{ pgettext('RentalServices', 'Inactive') }}
              </BFormCheckbox>
            </BDropdownForm>
          </BDropdown>
          <BDropdown
            size="sm"
            variant="light"
            toggle-class="border text-wrap text-start"
          >
            <template #button-content>
              <i :class="'fas ' + (sortOrderIcon || '') + ' me-1'"> </i>
              <span>
                {{ sortOrderLabel }}
              </span>
            </template>
            <BDropdownGroup :header="pgettext('RentalServices', 'Order')">
              <BDropdownItem
                :key="option.key"
                v-for="option in sortOptions"
                @click.prevent="
                  evh_4067533979448964_clickPrevent($event, { option })
                "
              >
                <i
                  :class="
                    sortOrder === option.key
                      ? 'fas fa-check me-2'
                      : 'fas fa-check me-2 invisible'
                  "
                >
                </i>
                <i :class="'fas ' + option.icon + ' me-2 text-muted'"> </i>
                <span>
                  {{ option.label }}
                </span>
              </BDropdownItem>
            </BDropdownGroup>
          </BDropdown>
        </div>
      </div>
      <BListGroup class="rental__services list-group-flush" v-if="loaded">
        <BListGroupItem v-if="!items.length">
          <span>
            {{ pgettext('RentalServices', 'No services.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem v-if="items.length > 0 && !visibleItems.length">
          <span class="text-muted">
            {{
              pgettext(
                'RentalServices',
                'No services match the current filters.'
              )
            }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-row align-items-start"
          v-for="item in visibleItems"
        >
          <UcInactive :value="item.inactive" class="me-2"> </UcInactive>
          <ItpBox direction="column" class="flex-grow-1" style="min-width: 0">
            <ItpBox alignItems="center" wrap-items :column-gap="2" class="mb-2">
              <ItpLink
                :to="{
                  name: 'app.rental',
                  params: { id: item.rental.id },
                }"
                class="me-2"
                v-if="rental == null"
              >
                <ItpText strong :text="item.rental.name"> </ItpText>
              </ItpLink>
              <UcCompactMeterDisplay
                :payload="item.meter"
                class="me-2"
                v-if="meter == null"
              >
              </UcCompactMeterDisplay>
              <ItpText
                :strikethrough="isServiceStale(item)"
                wrap
                class="me-2 fw-bold"
              >
                {{ item.service.name }} ({{ item.billing_unit }})
              </ItpText>
              <ItpLink
                :icon="$config.externalLinkIcon"
                :to="{ name: 'app.product', params: { id: item.service.id } }"
                class="me-2"
              >
              </ItpLink>
              <UcValidityPeriod
                :from="item.valid_from"
                :to="item.valid_to"
                class="me-2"
                v-if="item.valid_from || item.valid_to"
              >
              </UcValidityPeriod>
            </ItpBox>
            <ItpBox alignItems="center" wrap-items :column-gap="2">
              <UcBillingMultiplierBadge
                :value="item.meter_factor"
                class="me-2"
                v-if="item.meter_factor != null && item.meter_factor != 1"
              >
              </UcBillingMultiplierBadge>
              <UcTariffCodeBadge
                :value="item.tariff_code"
                class="me-2"
                v-if="item.tariff_code"
              >
              </UcTariffCodeBadge>
              <ItpBadge
                variant="warning"
                :text="pgettext('RentalService', 'Non billable')"
                no-box-shadow
                class="me-2 border"
                v-if="item.non_billable"
              >
              </ItpBadge>
            </ItpBox>
            <ItpBox alignItems="center" wrap-items :column-gap="2">
              <ItpText :text="item.notes" pre v-if="item.notes"> </ItpText>
            </ItpBox>
            <UcRentalServiceCostAllocators
              :service="item"
              v-if="
                item.cost_allocators &&
                item.cost_allocators.data &&
                item.cost_allocators.data.length
              "
            >
            </UcRentalServiceCostAllocators>
          </ItpBox>
          <UcDropdownMenu>
            <BDropdownItem
              @click.prevent.stop="
                evh_682765494101086_clickPreventStop($event, { item })
              "
            >
              <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
            </BDropdownItem>
            <BDropdownItem
              @click.prevent.stop="
                evh_7500099074451545_clickPreventStop($event, { item })
              "
            >
              <ItpText
                :text="
                  pgettext('RentalServiceCostAllocators', 'Add cost allocator')
                "
              >
              </ItpText>
            </BDropdownItem>
          </UcDropdownMenu>
        </BListGroupItem>
      </BListGroup>
    </div>
  </ItpUsercontrol>
</template>
